import { useMemo, useSyncExternalStore } from "react";

import { useGetFeatureSubscriptionsQuery } from "services/paywall";
import type { PaywallFeatureSubscription } from "services/types/paywall.type";

/**
 * A Scribe purchase that Stripe has sent the user back from, kept until the webhook makes it live
 * (SN-19868). Only `ScribePurchaseReturn` writes it, and only on a success return, so leaving Stripe
 * with the back button never sets it. The purchase screen reads it to block an obvious second
 * payment; the real guard against paying twice is the backend's duplicate-payment check.
 *
 * Kept in sessionStorage so a reload while the purchase finishes picks the wait back up. The
 * in-memory copy is the source of truth for this page load, so blocked storage (a private window)
 * still works until the page is left.
 */
const MARKER_KEY = "suno_scribe_purchase_return_v1";
const MARKER_EVENT = "suno:scribe-purchase-marker";

/** Older than this, a marker is ignored: whatever was waiting on it is long gone. */
export const PURCHASE_MARKER_FRESH_MS = 15 * 60 * 1000;

/** The statuses the webhook leaves a paid row in. */
const LIVE_STATUSES = new Set(["active", "trialing"]);

export interface ScribePurchaseMarker {
  subscriptionId: number;
  /** When Stripe sent the user back, in epoch milliseconds. */
  at: number;
}

let current: string | null | undefined;

const snapshot = (): string | null => {
  if (current === undefined) {
    try {
      current = window.sessionStorage.getItem(MARKER_KEY);
    } catch {
      current = null;
    }
  }
  return current;
};

const subscribe = (onChange: () => void) => {
  window.addEventListener(MARKER_EVENT, onChange);
  return () => window.removeEventListener(MARKER_EVENT, onChange);
};

const store = (value: string | null) => {
  current = value;
  try {
    if (value === null) window.sessionStorage.removeItem(MARKER_KEY);
    else window.sessionStorage.setItem(MARKER_KEY, value);
  } catch {
    // Blocked storage: the in-memory copy still covers this page load.
  }
  window.dispatchEvent(new Event(MARKER_EVENT));
};

export const writeScribePurchaseMarker = (marker: ScribePurchaseMarker) =>
  store(JSON.stringify(marker));

export const clearScribePurchaseMarker = () => store(null);

function parseMarker(raw: string | null): ScribePurchaseMarker | null {
  if (!raw) return null;
  try {
    const { subscriptionId, at } = JSON.parse(raw);
    return Number.isSafeInteger(subscriptionId) && Number.isFinite(at)
      ? { subscriptionId, at }
      : null;
  } catch {
    return null;
  }
}

/** The purchase being finished, or `null` when there is none or it is stale. */
export function useScribePurchaseMarker(): ScribePurchaseMarker | null {
  const raw = useSyncExternalStore(subscribe, snapshot);
  const marker = useMemo(() => parseMarker(raw), [raw]);
  return marker && Date.now() - marker.at < PURCHASE_MARKER_FRESH_MS
    ? marker
    : null;
}

export const isPurchasedRowLive = (
  rows: PaywallFeatureSubscription[] | undefined,
  subscriptionId: number
) =>
  Boolean(
    rows?.some(
      (row) =>
        row.id === subscriptionId && LIVE_STATUSES.has(row.status ?? "")
    )
  );

/**
 * The purchase screen's view of a purchase Stripe sent the user back from. `ScribePurchaseReturn`
 * polls the subscriptions list; this reads the same cache entry (the same `limit: 100` as
 * `useAmbientScribeTrialStatus`), so it sees the row go live on the same poll.
 *
 * `isFinishing`: the purchase is not live yet. `isLive`: it just went live, while the marker is
 * still there — the one render in which the screen can tell a finished purchase from a timeout.
 */
export function useScribePurchaseInProgress(enabled: boolean) {
  const marker = useScribePurchaseMarker();
  const subscriptionId = enabled ? (marker?.subscriptionId ?? null) : null;
  const { data } = useGetFeatureSubscriptionsQuery(
    { limit: 100 },
    { skip: subscriptionId === null }
  );
  // Gated on the marker as well as `skip`: RTK keeps the last `data` when a query is skipped.
  const isLive =
    subscriptionId !== null && isPurchasedRowLive(data, subscriptionId);
  return { isFinishing: subscriptionId !== null && !isLive, isLive };
}
